import React from "react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, Filter, X, ListPlus } from "lucide-react";
import { initials } from "@/lib/format";

export const GROUP_OPTIONS = [
  { key: "status", label: "Status" },
  { key: "priority", label: "Priority" },
  { key: "assignee", label: "Assignee" },
  { key: "none", label: "None" },
];

export const TIME_FILTERS = [
  { key: "any", label: "Any time" },
  { key: "none", label: "No time logged" },
  { key: "lt1h", label: "Less than 1h" },
  { key: "1to4h", label: "1h – 4h" },
  { key: "gt4h", label: "More than 4h" },
];

export const DUE_FILTERS = [
  { key: "any", label: "Any due date" },
  { key: "overdue", label: "Overdue" },
  { key: "today", label: "Due today" },
  { key: "week", label: "This week" },
  { key: "none", label: "No due date" },
];

export default function FilterBar({
  filters, onChange, groupBy, onGroupBy, statuses, users, canEdit, onQuickAdd,
}) {
  const setF = (patch) => onChange({ ...filters, ...patch });
  const activeCount =
    (filters.q ? 1 : 0) +
    (filters.status && filters.status !== "any" ? 1 : 0) +
    (filters.priority && filters.priority !== "any" ? 1 : 0) +
    (filters.assignee && filters.assignee !== "any" ? 1 : 0) +
    (filters.due && filters.due !== "any" ? 1 : 0) +
    (filters.time && filters.time !== "any" ? 1 : 0);

  const clear = () => onChange({ q: "", status: "any", priority: "any", assignee: "any", due: "any", time: "any" });

  return (
    <div className="px-6 pt-4 flex flex-wrap items-center gap-2" data-testid="filter-bar">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
        <Input
          value={filters.q || ""}
          onChange={(e) => setF({ q: e.target.value })}
          placeholder="Search tasks…"
          className="h-9 pl-8 rounded-sm w-56 bg-white"
          data-testid="filter-search-input"
        />
      </div>

      <div className="flex items-center gap-1 text-xs text-neutral-500 ml-1">
        <Filter className="w-3.5 h-3.5" />
        <span className="font-mono-data text-[10px] uppercase tracking-[0.15em]">Filters</span>
      </div>

      <Select value={filters.status || "any"} onValueChange={(v) => setF({ status: v })}>
        <SelectTrigger className="h-9 w-36 rounded-sm bg-white" data-testid="filter-status-select"><SelectValue placeholder="Status" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="any">All statuses</SelectItem>
          {statuses.map((s) => (
            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filters.priority || "any"} onValueChange={(v) => setF({ priority: v })}>
        <SelectTrigger className="h-9 w-32 rounded-sm bg-white" data-testid="filter-priority-select"><SelectValue placeholder="Priority" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="any">All priorities</SelectItem>
          {["urgent", "high", "medium", "low"].map((p) => (
            <SelectItem key={p} value={p}>{p}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filters.assignee || "any"} onValueChange={(v) => setF({ assignee: v })}>
        <SelectTrigger className="h-9 w-40 rounded-sm bg-white" data-testid="filter-assignee-select"><SelectValue placeholder="Assignee" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="any">All assignees</SelectItem>
          <SelectItem value="__none__">Unassigned</SelectItem>
          {users.map((u) => (
            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filters.due || "any"} onValueChange={(v) => setF({ due: v })}>
        <SelectTrigger className="h-9 w-36 rounded-sm bg-white" data-testid="filter-due-select"><SelectValue placeholder="Due date" /></SelectTrigger>
        <SelectContent>
          {DUE_FILTERS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filters.time || "any"} onValueChange={(v) => setF({ time: v })}>
        <SelectTrigger className="h-9 w-36 rounded-sm bg-white" data-testid="filter-time-select"><SelectValue placeholder="Time spent" /></SelectTrigger>
        <SelectContent>
          {TIME_FILTERS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
        </SelectContent>
      </Select>

      {activeCount > 0 && (
        <Button
          variant="ghost"
          onClick={clear}
          className="h-9 rounded-sm text-xs"
          data-testid="filter-clear-button"
        >
          <X className="w-3.5 h-3.5 mr-1" /> Clear ({activeCount})
        </Button>
      )}

      <div className="ml-auto flex items-center gap-2">
        <div className="flex items-center gap-1 text-xs text-neutral-500">
          <span className="font-mono-data text-[10px] uppercase tracking-[0.15em]">Group by</span>
        </div>
        <Select value={groupBy} onValueChange={onGroupBy}>
          <SelectTrigger className="h-9 w-32 rounded-sm bg-white" data-testid="group-by-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {GROUP_OPTIONS.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// Pure filtering helper — exported for use in views
export function applyFilters(tasks, filters) {
  const q = (filters.q || "").trim().toLowerCase();
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(startOfToday.getTime() + 24 * 3600 * 1000);
  const weekEnd = new Date(startOfToday.getTime() + 7 * 24 * 3600 * 1000);

  return tasks.filter((t) => {
    if (q && !t.title.toLowerCase().includes(q) && !(t.description || "").toLowerCase().includes(q)) return false;
    if (filters.status && filters.status !== "any" && t.status_id !== filters.status) return false;
    if (filters.priority && filters.priority !== "any" && t.priority !== filters.priority) return false;
    if (filters.assignee && filters.assignee !== "any") {
      const ids = Array.isArray(t.assignee_ids) ? t.assignee_ids : (t.assignee_id ? [t.assignee_id] : []);
      if (filters.assignee === "__none__") {
        if (ids.length > 0) return false;
      } else if (!ids.includes(filters.assignee)) return false;
    }
    if (filters.due && filters.due !== "any") {
      const due = t.due_date ? new Date(t.due_date) : null;
      switch (filters.due) {
        case "overdue": if (!due || due >= startOfToday) return false; break;
        case "today":   if (!due || due < startOfToday || due >= endOfToday) return false; break;
        case "week":    if (!due || due < startOfToday || due >= weekEnd) return false; break;
        case "none":    if (due) return false; break;
        default: break;
      }
    }
    if (filters.time && filters.time !== "any") {
      const sec = t.total_time_seconds || 0;
      switch (filters.time) {
        case "none":   if (sec > 0) return false; break;
        case "lt1h":   if (sec >= 3600 || sec === 0) return false; break;
        case "1to4h":  if (sec < 3600 || sec > 4 * 3600) return false; break;
        case "gt4h":   if (sec <= 4 * 3600) return false; break;
        default: break;
      }
    }
    return true;
  });
}
